import { assertNever } from "@aistihealth/web-common/src/typings/typeUtils"
import { rem, transparentize } from "polished"
import styled, { css, FlattenSimpleInterpolation } from "styled-components"
import { theme } from "@aistihealth/web-common/src/util/styles/theme"

export const inputSizes = ["small", "medium"] as const
export type InputSize = typeof inputSizes[number]

const inputBorderSize = rem(1)

export type InputProps = {
  // TODO Would be nicer to call this just size, but seems to clash with input's size attr
  // There should be some way around!
  componentSize?: InputSize
  disabled?: boolean
  error?: boolean
}

export const DisabledInputStyles = css`
  color: ${transparentize(0.5, theme.colors.black)};
  cursor: not-allowed;
  opacity: 0.7;
  background: ${theme.colors.grey};
  border-color: ${theme.colors.greyDark};
`

const inputSizeStyles: Record<InputSize, FlattenSimpleInterpolation> = {
  small: css`
    ${theme.textStyle("default", "small", "normal")};
    padding: calc(${theme.spacings.small} - ${inputBorderSize})
      calc(${theme.spacings.small} - ${inputBorderSize});
    height: 2rem;
    min-height: 2rem;
    border-radius: ${theme.borderRadius.xsmall};
  `,
  medium: css`
    ${theme.textStyle("default", "medium", "normal")};
    padding: calc(${theme.spacings.medium} - ${inputBorderSize})
      calc(${theme.spacings.medium} / 2 - ${inputBorderSize});
    height: 3rem;
    min-height: 3rem;
    border-radius: ${theme.borderRadius.small};
  `,
}

export const BaseInputStyles = css<InputProps>`
  appearance: none;
  background: ${theme.colors.white};
  border: ${inputBorderSize} solid
    ${({ error }) => (error ? theme.colors.red : theme.colors.greyDark)};
  color: ${theme.colors.primary};
  outline: none;
  ${({ componentSize }) => {
    switch (componentSize) {
      case "small":
      case "medium":
        return inputSizeStyles[componentSize]
      default:
        return assertNever(componentSize)
    }
  }}
  transition: border-color 0.3s;
  width: 100%;

  ::placeholder {
    color: ${transparentize(0.5, theme.colors.black)};
  }

  :focus {
    border: ${inputBorderSize} solid
      ${({ error }) => (error ? theme.colors.red : theme.colors.blueBright)};
  }

  &:disabled {
    ${DisabledInputStyles};
  }
`

export const BaseInput = styled.input<InputProps>`
  ${BaseInputStyles}
`

BaseInput.defaultProps = {
  error: false,
  componentSize: "medium",
}
